import { QueryFunctionContext, useQuery } from 'react-query'
import produce, { castImmutable } from 'immer'
import { TRoutineCreatedResponse, showRoutinePlacementReceipt } from '../../components/routines/routinePlacementReceipt'
import useQueryContext from '../../context/QueryContext'
import apiClient from '../../utils/api'
import {
    appendOptimisticRoutineItem,
    applyRoutineCompletionToOverview,
    applyRoutineItemCompletion,
    removeOptimisticRoutineItem,
    resolveOptimisticRoutineItem,
} from '../../utils/routines'
import { TOverviewView, TRoutine } from '../../utils/types'
import {
    OptimisticTransaction,
    beginOptimisticTransaction,
    optimisticQueryChange,
    reconcileOptimisticTransaction,
    rollbackOptimisticTransaction,
} from '../optimisticTransaction'
import { useGTMutation, useGTQueryClient } from '../queryUtils'

export interface RoutineDraft {
    name: string
    items: { title: string }[]
}

interface OptimisticRoutineDraft extends RoutineDraft {
    optimisticId: string
}

interface RoutineMutationContext<TResponse = unknown> {
    transaction?: OptimisticTransaction<TResponse>
}

export const useGetRoutines = () => useQuery<TRoutine[], void>('routines', getRoutines)

const getRoutines = async ({ signal }: QueryFunctionContext) => {
    const response = await apiClient.get('/routines/', { signal })
    return castImmutable(response.data)
}

const routineMutationOptions = {
    tag: 'routines',
    invalidateTagsOnSettled: ['routines', 'overview', 'overview-supported-views'],
}

const optimisticRoutine = (draft: RoutineDraft, optimisticId: string, idOrdering: number): TRoutine => {
    const items = draft.items.map((item, index) => ({
        id: `${optimisticId}-item-${index + 1}`,
        optimisticId: `${optimisticId}-item-${index + 1}`,
        title: item.title,
        id_ordering: index + 1,
        is_completed: false,
    }))
    return {
        id: optimisticId,
        optimisticId,
        name: draft.name,
        id_ordering: idOrdering,
        status: items.length === 0 ? 'empty' : 'up_next',
        remaining_count: items.length,
        streak: 0,
        items,
    }
}

const reconcileOptimisticRoutine = (routine: TRoutine, response: TRoutineCreatedResponse): TRoutine => {
    const initialItemPrefix = `${routine.optimisticId}-item-`
    const initialItems = routine.items.filter((item) => item.optimisticId?.startsWith(initialItemPrefix))
    const hasEveryItemId = response.item_ids !== undefined && response.item_ids.length === initialItems.length
    if (!hasEveryItemId) return { ...routine, id: response.id }
    let initialItemIndex = 0
    return {
        ...routine,
        id: response.id,
        optimisticId: undefined,
        items: routine.items.map((item) => {
            if (!item.optimisticId?.startsWith(initialItemPrefix)) return item
            const id = response.item_ids?.[initialItemIndex] ?? item.id
            initialItemIndex += 1
            return { ...item, id, optimisticId: undefined }
        }),
    }
}

const optimisticRoutineCreateChange = (draft: OptimisticRoutineDraft) =>
    optimisticQueryChange<TRoutine[], TRoutineCreatedResponse>(
        'routines',
        (current) => [...(current ?? []), optimisticRoutine(draft, draft.optimisticId, (current?.length ?? 0) + 1)],
        {
            rollback: (current) => current?.filter((routine) => routine.optimisticId !== draft.optimisticId),
            reconcile: (current, response) =>
                current?.map((routine) =>
                    routine.optimisticId === draft.optimisticId
                        ? reconcileOptimisticRoutine(routine, response)
                        : routine
                ),
        }
    )

const optimisticRoutineDuplicateChange = (
    sourceId: string,
    optimisticId: string,
    getIdFromOptimisticId: (id: string) => string | undefined
) =>
    optimisticQueryChange<TRoutine[], TRoutineCreatedResponse>(
        'routines',
        (current) => {
            if (!current) return current
            const resolvedSourceId = getIdFromOptimisticId(sourceId)
            const sourceIndex = current.findIndex(
                (routine) =>
                    routine.id === sourceId || routine.id === resolvedSourceId || routine.optimisticId === sourceId
            )
            if (sourceIndex === -1) return current
            const source = current[sourceIndex]
            const duplicate = optimisticRoutine(
                { name: `${source.name} copy`, items: source.items.map(({ title }) => ({ title })) },
                optimisticId,
                source.id_ordering + 1
            )
            return [...current.slice(0, sourceIndex + 1), duplicate, ...current.slice(sourceIndex + 1)]
        },
        {
            rollback: (current) => current?.filter((routine) => routine.optimisticId !== optimisticId),
            reconcile: (current, response) =>
                current?.map((routine) =>
                    routine.optimisticId === optimisticId ? reconcileOptimisticRoutine(routine, response) : routine
                ),
        }
    )

const rollbackRoutineModify = (
    current: TRoutine[] | undefined,
    previous: TRoutine[] | undefined,
    optimistic: TRoutine[] | undefined,
    data: ModifyRoutineData,
    getIdFromOptimisticId: (id: string) => string | undefined
) => {
    if (!current || !previous) return current
    const resolvedId = getIdFromOptimisticId(data.id)
    const matchesRoutine = (routine: TRoutine) =>
        routine.id === data.id || routine.id === resolvedId || routine.optimisticId === data.id
    const previousRoutine = previous.find(matchesRoutine)
    if (!previousRoutine) return current
    if (data.is_deleted) {
        if (current.some(matchesRoutine)) return current
        const index = previous.findIndex(matchesRoutine)
        return [...current.slice(0, index), previousRoutine, ...current.slice(index)]
    }
    const optimisticName = optimistic?.find(matchesRoutine)?.name
    return current.map((routine) =>
        matchesRoutine(routine) && routine.name === optimisticName
            ? { ...routine, name: previousRoutine.name }
            : routine
    )
}

const rollbackRoutineOverviewModify = (
    current: TOverviewView[] | undefined,
    previous: TOverviewView[] | undefined,
    optimistic: TOverviewView[] | undefined,
    data: ModifyRoutineData,
    getIdFromOptimisticId: (id: string) => string | undefined
) => {
    if (!current || !previous) return current
    const resolvedId = getIdFromOptimisticId(data.id)
    const matchesView = (view: TOverviewView) => view.routine_id === data.id || view.routine_id === resolvedId
    const previousView = previous.find(matchesView)
    if (!previousView) return current
    if (data.is_deleted) {
        if (current.some(matchesView)) return current
        const index = previous.findIndex(matchesView)
        return [...current.slice(0, index), previousView, ...current.slice(index)]
    }
    const optimisticName = optimistic?.find(matchesView)?.name
    return current.map((view) =>
        matchesView(view) && view.name === optimisticName ? { ...view, name: previousView.name } : view
    )
}

const routineModifyChanges = (data: ModifyRoutineData, getIdFromOptimisticId: (id: string) => string | undefined) => [
    optimisticQueryChange<TRoutine[]>(
        'routines',
        (current) => {
            if (!current) return current
            const resolvedId = getIdFromOptimisticId(data.id)
            if (data.is_deleted)
                return current.filter(
                    (routine) => routine.id !== data.id && routine.id !== resolvedId && routine.optimisticId !== data.id
                )
            return current.map((routine) =>
                routine.id === data.id || routine.id === resolvedId || routine.optimisticId === data.id
                    ? { ...routine, name: data.name ?? routine.name }
                    : routine
            )
        },
        {
            rollback: (current, previous, optimistic) =>
                rollbackRoutineModify(current, previous, optimistic, data, getIdFromOptimisticId),
        }
    ),
    optimisticQueryChange<TOverviewView[]>(
        'overview',
        (current) => {
            if (!current) return current
            const resolvedId = getIdFromOptimisticId(data.id)
            if (data.is_deleted)
                return current.filter((view) => view.routine_id !== data.id && view.routine_id !== resolvedId)
            return current.map((view) =>
                view.routine_id === data.id || view.routine_id === resolvedId
                    ? { ...view, name: data.name ?? view.name }
                    : view
            )
        },
        {
            rollback: (current, previous, optimistic) =>
                rollbackRoutineOverviewModify(current, previous, optimistic, data, getIdFromOptimisticId),
        }
    ),
]

const updateRoutineItem = (
    routines: TRoutine[] | undefined,
    data: ModifyRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) =>
    routines
        ? produce(routines, (draft) => {
              for (const routine of draft) {
                  const resolvedId = getIdFromOptimisticId(data.id)
                  const index = routine.items.findIndex(
                      (item) => item.id === data.id || item.id === resolvedId || item.optimisticId === data.id
                  )
                  if (index === -1) continue
                  if (data.is_deleted) routine.items.splice(index, 1)
                  else if (data.title !== undefined) routine.items[index].title = data.title
                  routine.remaining_count = routine.items.filter((item) => !item.is_completed).length
                  return
              }
          })
        : routines

const updateOverviewRoutineItem = (
    views: TOverviewView[] | undefined,
    data: ModifyRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) =>
    views
        ? produce(views, (draft) => {
              for (const view of draft) {
                  if (view.type !== 'routine') continue
                  const items = view.view_items
                  const resolvedId = getIdFromOptimisticId(data.id)
                  const index = items.findIndex((item) => item.id === data.id || item.id === resolvedId)
                  if (index === -1) continue
                  if (data.is_deleted) {
                      items.splice(index, 1)
                      view.view_item_ids = view.view_item_ids.filter((id) => id !== data.id && id !== resolvedId)
                  } else if (data.title !== undefined) items[index].title = data.title
                  view.remaining_count = items.filter((item) => 'is_completed' in item && !item.is_completed).length
                  return
              }
          })
        : views

const rollbackRoutineItem = (
    current: TRoutine[] | undefined,
    previous: TRoutine[] | undefined,
    optimistic: TRoutine[] | undefined,
    data: ModifyRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) => {
    if (!current || !previous) return current
    const resolvedId = getIdFromOptimisticId(data.id)
    const matchesItem = (item: { id: string; optimisticId?: string }) =>
        item.id === data.id || item.id === resolvedId || item.optimisticId === data.id
    const priorRoutine = previous.find((routine) => routine.items.some(matchesItem))
    const priorItem = priorRoutine?.items.find(matchesItem)
    if (!priorRoutine || !priorItem) return current
    if (data.is_deleted) {
        return produce(current, (draft) => {
            const routine = draft.find((candidate) => candidate.id === priorRoutine.id)
            if (!routine || routine.items.some(matchesItem)) return
            const index = priorRoutine.items.findIndex(matchesItem)
            routine.items.splice(index, 0, priorItem)
            routine.remaining_count = routine.items.filter((item) => !item.is_completed).length
        })
    }
    const optimisticTitle = optimistic?.flatMap((routine) => routine.items).find(matchesItem)?.title
    return produce(current, (draft) => {
        const item = draft.flatMap((routine) => routine.items).find(matchesItem)
        if (item && item.title === optimisticTitle) item.title = priorItem.title
    })
}

const rollbackOverviewRoutineItem = (
    current: TOverviewView[] | undefined,
    previous: TOverviewView[] | undefined,
    optimistic: TOverviewView[] | undefined,
    data: ModifyRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) => {
    if (!current || !previous) return current
    const resolvedId = getIdFromOptimisticId(data.id)
    const matchesItem = (item: { id: string }) => item.id === data.id || item.id === resolvedId
    const priorView = previous.find((view) => view.view_items.some(matchesItem))
    const priorItem = priorView?.view_items.find(matchesItem)
    if (!priorView || !priorItem) return current
    if (data.is_deleted) {
        return produce(current, (draft) => {
            const view = draft.find((candidate) => candidate.id === priorView.id)
            if (!view || view.view_items.some(matchesItem)) return
            const index = priorView.view_items.findIndex(matchesItem)
            view.view_items.splice(index, 0, priorItem)
            view.view_item_ids.splice(index, 0, data.id)
            view.remaining_count = view.view_items.filter((item) => 'is_completed' in item && !item.is_completed).length
        })
    }
    const optimisticTitle = optimistic?.flatMap((view) => view.view_items).find(matchesItem)?.title
    return produce(current, (draft) => {
        const item = draft.flatMap((view) => view.view_items).find(matchesItem)
        if (item && item.title === optimisticTitle) item.title = priorItem.title
    })
}

const routineItemModifyChanges = (
    data: ModifyRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) => [
    optimisticQueryChange<TRoutine[]>(
        'routines',
        (current) => updateRoutineItem(current, data, getIdFromOptimisticId),
        {
            rollback: (current, previous, optimistic) =>
                rollbackRoutineItem(current, previous, optimistic, data, getIdFromOptimisticId),
        }
    ),
    optimisticQueryChange<TOverviewView[]>(
        'overview',
        (current) => updateOverviewRoutineItem(current, data, getIdFromOptimisticId),
        {
            rollback: (current, previous, optimistic) =>
                rollbackOverviewRoutineItem(current, previous, optimistic, data, getIdFromOptimisticId),
        }
    ),
]

export const useCreateRoutine = () => {
    const queryClient = useGTQueryClient()
    const { setOptimisticId } = useQueryContext()
    return useGTMutation<TRoutineCreatedResponse, unknown, OptimisticRoutineDraft, RoutineMutationContext>(
        async ({ optimisticId: _optimisticId, ...draft }) => {
            const response = await apiClient.post<TRoutineCreatedResponse>('/routines/', draft)
            return response.data
        },
        {
            ...routineMutationOptions,
            errorMessage: 'create routine',
            onMutate: (draft) => {
                void queryClient.cancelQueries('routines')
                return {
                    transaction: beginOptimisticTransaction(queryClient, [optimisticRoutineCreateChange(draft)]),
                }
            },
            onSuccess: (data, draft, context) => {
                setOptimisticId(draft.optimisticId, data.id)
                data.item_ids?.forEach((id, index) => setOptimisticId(`${draft.optimisticId}-item-${index + 1}`, id))
                reconcileOptimisticTransaction(context?.transaction, data)
                showRoutinePlacementReceipt(queryClient, draft.name, data.id, data)
            },
            onError: (_error, _draft, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}

interface ModifyRoutineData {
    id: string
    name?: string
    is_deleted?: boolean
}

export const useModifyRoutine = () => {
    const queryClient = useGTQueryClient()
    const { getIdFromOptimisticId } = useQueryContext()
    return useGTMutation<unknown, unknown, ModifyRoutineData, RoutineMutationContext>(
        ({ id, ...patch }) => apiClient.patch(`/routines/${id}/`, patch),
        {
            ...routineMutationOptions,
            errorMessage: 'update routine',
            onMutate: (data) => {
                if (data.name === undefined && !data.is_deleted) return {}
                void queryClient.cancelQueries('routines')
                void queryClient.cancelQueries('overview')
                return {
                    transaction: beginOptimisticTransaction(
                        queryClient,
                        routineModifyChanges(data, getIdFromOptimisticId)
                    ),
                }
            },
            onSuccess: (response, _data, context) => reconcileOptimisticTransaction(context?.transaction, response),
            onError: (_error, _data, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}

export const useDuplicateRoutine = () => {
    const queryClient = useGTQueryClient()
    const { getIdFromOptimisticId, setOptimisticId } = useQueryContext()
    return useGTMutation<
        TRoutineCreatedResponse,
        unknown,
        { id: string; optimisticId: string },
        RoutineMutationContext
    >(
        async ({ id }) => {
            const response = await apiClient.post<TRoutineCreatedResponse>(`/routines/${id}/duplicate/`)
            return response.data
        },
        {
            ...routineMutationOptions,
            errorMessage: 'duplicate routine',
            onMutate: ({ id, optimisticId }) => {
                void queryClient.cancelQueries('routines')
                return {
                    transaction: beginOptimisticTransaction(queryClient, [
                        optimisticRoutineDuplicateChange(id, optimisticId, getIdFromOptimisticId),
                    ]),
                }
            },
            onSuccess: (data, { id, optimisticId }, context) => {
                setOptimisticId(optimisticId, data.id)
                data.item_ids?.forEach((itemId, index) => setOptimisticId(`${optimisticId}-item-${index + 1}`, itemId))
                reconcileOptimisticTransaction(context?.transaction, data)
                const source = queryClient.getQueryData<TRoutine[]>('routines')?.find((routine) => routine.id === id)
                const name = source ? `${source.name} copy` : 'Routine copy'
                showRoutinePlacementReceipt(queryClient, name, data.id, data)
            },
            onError: (_error, _data, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}

interface TCreateRoutineItemData {
    id: string
    title: string
    optimisticId: string
}

const optimisticRoutineItemCreateChange = (
    data: TCreateRoutineItemData,
    getIdFromOptimisticId: (id: string) => string | undefined
) =>
    optimisticQueryChange<TRoutine[], { id: string }>(
        'routines',
        (current) => {
            if (!current) return current
            const routineId = getIdFromOptimisticId(data.id) ?? data.id
            return appendOptimisticRoutineItem(current, routineId, data.optimisticId, data.title)
        },
        {
            rollback: (current) => (current ? removeOptimisticRoutineItem(current, data.optimisticId) : current),
            reconcile: (current, response) =>
                current ? resolveOptimisticRoutineItem(current, data.optimisticId, response.id) : current,
        }
    )

export const useCreateRoutineItem = () => {
    const queryClient = useGTQueryClient()
    const { getIdFromOptimisticId, setOptimisticId } = useQueryContext()
    return useGTMutation<{ id: string }, unknown, TCreateRoutineItemData, RoutineMutationContext<{ id: string }>>(
        async ({ id, title }: TCreateRoutineItemData) => {
            const response = await apiClient.post<{ id: string }>(`/routines/${id}/items/`, { title })
            return response.data
        },
        {
            ...routineMutationOptions,
            errorMessage: 'add routine step',
            onMutate: ({ id, title, optimisticId }) => {
                void queryClient.cancelQueries('routines')
                return {
                    transaction: beginOptimisticTransaction(queryClient, [
                        optimisticRoutineItemCreateChange({ id, title, optimisticId }, getIdFromOptimisticId),
                    ]),
                }
            },
            onSuccess: ({ id }, { optimisticId }, context) => {
                setOptimisticId(optimisticId, id)
                reconcileOptimisticTransaction(context?.transaction, { id })
            },
            onError: (_error, _data, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}

interface ModifyRoutineItemData {
    id: string
    title?: string
    id_ordering?: number
    is_deleted?: boolean
}

export const useModifyRoutineItem = () => {
    const queryClient = useGTQueryClient()
    const { getIdFromOptimisticId } = useQueryContext()
    return useGTMutation<unknown, unknown, ModifyRoutineItemData, RoutineMutationContext>(
        ({ id, ...patch }) => apiClient.patch(`/routine_items/${id}/`, patch),
        {
            ...routineMutationOptions,
            errorMessage: 'update routine step',
            onMutate: (data) => {
                if (data.id_ordering !== undefined || (data.title === undefined && !data.is_deleted)) return {}
                void queryClient.cancelQueries('routines')
                void queryClient.cancelQueries('overview')
                return {
                    transaction: beginOptimisticTransaction(
                        queryClient,
                        routineItemModifyChanges(data, getIdFromOptimisticId)
                    ),
                }
            },
            onSuccess: (response, _data, context) => reconcileOptimisticTransaction(context?.transaction, response),
            onError: (_error, _data, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}

interface RoutineCompletionData {
    id: string
    is_completed: boolean
}

const routineItemCompletion = (routines: TRoutine[] | undefined, itemId: string) =>
    routines?.flatMap((routine) => routine.items).find((item) => item.id === itemId)?.is_completed

const overviewRoutineItemCompletion = (views: TOverviewView[] | undefined, itemId: string) => {
    const item = views?.flatMap((view) => view.view_items).find((candidate) => candidate.id === itemId)
    return item && 'is_completed' in item ? item.is_completed : undefined
}

const routineCompletionChanges = ({ id, is_completed }: RoutineCompletionData) => [
    optimisticQueryChange<TRoutine[]>(
        'routines',
        (current) => (current ? applyRoutineItemCompletion(current, id, is_completed) : current),
        {
            rollback: (current, previous, optimistic) => {
                const previousCompletion = routineItemCompletion(previous, id)
                const optimisticCompletion = routineItemCompletion(optimistic, id)
                if (
                    !current ||
                    previousCompletion === undefined ||
                    optimisticCompletion === undefined ||
                    routineItemCompletion(current, id) !== optimisticCompletion
                )
                    return current
                return applyRoutineItemCompletion(current, id, previousCompletion)
            },
        }
    ),
    optimisticQueryChange<TOverviewView[]>(
        'overview',
        (current) => (current ? applyRoutineCompletionToOverview(current, id, is_completed) : current),
        {
            rollback: (current, previous, optimistic) => {
                const previousCompletion = overviewRoutineItemCompletion(previous, id)
                const optimisticCompletion = overviewRoutineItemCompletion(optimistic, id)
                if (
                    !current ||
                    previousCompletion === undefined ||
                    optimisticCompletion === undefined ||
                    overviewRoutineItemCompletion(current, id) !== optimisticCompletion
                )
                    return current
                return applyRoutineCompletionToOverview(current, id, previousCompletion)
            },
        }
    ),
]

export const useCompleteRoutineItem = () => {
    const queryClient = useGTQueryClient()
    return useGTMutation<unknown, unknown, RoutineCompletionData, RoutineMutationContext>(
        ({ id, is_completed }: RoutineCompletionData) =>
            apiClient.patch(`/routine_items/${id}/completion/`, { is_completed }),
        {
            ...routineMutationOptions,
            errorMessage: 'update routine step',
            onMutate: ({ id, is_completed }) => {
                void queryClient.cancelQueries('routines')
                void queryClient.cancelQueries('overview')
                return {
                    transaction: beginOptimisticTransaction(
                        queryClient,
                        routineCompletionChanges({ id, is_completed })
                    ),
                }
            },
            onSuccess: (response, _data, context) => reconcileOptimisticTransaction(context?.transaction, response),
            onError: (_error, _variables, context) => rollbackOptimisticTransaction(context?.transaction),
        }
    )
}
